import { toastError } from '@/lib/toast-error';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Building2, Pencil, Save, X, Plus, Trash2, RotateCcw, Info } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { OfficeManagerBoard } from '@/components/office-manager/OfficeManagerBoard';
import { KitsModule } from '@/components/kits/KitsModule';
import { useTranslation, Trans } from 'react-i18next';

interface OfficeRow {
  id: string;
  name: string;
  country: string;
  address: string;
  contactName?: string;
  phone?: string;
  phoneCountryCode?: string;
  source: 'quipteams' | 'custom';
  overridden: boolean;
}

interface Draft {
  name: string;
  country: string;
  address: string;
  contactName: string;
  phone: string;
}

const emptyDraft = (): Draft => ({ name: '', country: '', address: '', contactName: '', phone: '' });

/**
 * Tab "Oficinas" (Usuarios): direcciones de oficinas editables por People.
 * La lista base viene de Quipteams; los cambios se guardan como override y los
 * usan TODOS los flujos de la plataforma (autocompletados de soporte/reasignación
 * — incluidos los pedidos que se le mandan a Quipteams — y los mails de onboarding).
 * La API de Quipteams no permite editar sus oficinas: para su dashboard interno
 * hay que pedirlo a Quipteams (aviso en la página).
 */
export function OfficesPage() {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = trpc.config.getOffices.useQuery();
  const { data: omAccess } = trpc.officeManager.getMyAccess.useQuery(undefined, {
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const { data: kitsAccess } = trpc.kits.getMyAccess.useQuery(undefined, {
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  // Tabs controladas por ?tab= para poder deep-linkear (ej. Panel People → Kits)
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const tab = rawTab === 'office-manager' || rawTab === 'kits' ? rawTab : 'direcciones';
  const setTab = (next: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft());

  const saveOffice = trpc.config.saveOffice.useMutation({
    onSuccess: () => {
      toast.success(t('admin.offices.oficina_guardada'));
      setEditingId(null);
      setAdding(false);
      refetch();
    },
    onError: (e) => toastError(e, 'common.toast.error_al_guardar'),
  });
  const resetOffice = trpc.config.resetOffice.useMutation({
    onSuccess: () => { toast.success('Listo'); refetch(); },
    onError: (e) => toastError(e, 'common.toast.error_generico'),
  });

  const offices: OfficeRow[] = (data?.offices ?? []) as OfficeRow[];

  const startEdit = (o: OfficeRow) => {
    setAdding(false);
    setEditingId(o.id);
    setDraft({
      name: o.name,
      country: o.country,
      address: o.address,
      contactName: o.contactName ?? '',
      phone: o.phone ? `${o.phoneCountryCode ? o.phoneCountryCode + ' ' : ''}${o.phone}` : '',
    });
  };

  const submit = (o?: OfficeRow) => {
    saveOffice.mutate({
      id: o?.id,
      name: draft.name.trim(),
      country: draft.country.trim(),
      address: draft.address.trim(),
      contactName: draft.contactName.trim() || undefined,
      phone: draft.phone.trim() || undefined,
      source: o?.source ?? 'custom',
    });
  };

  const canSubmit = draft.name.trim().length >= 2 && draft.country.trim().length >= 2 && draft.address.trim().length >= 5;

  const draftCells = (
    <>
      <TableCell><Input value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} placeholder={t('admin.offices.winclap_ciudad')} className="h-8" /></TableCell>
      <TableCell><Input value={draft.country} onChange={(e) => setDraft((d) => ({ ...d, country: e.target.value }))} placeholder="Argentina" className="h-8" /></TableCell>
      <TableCell><Input value={draft.address} onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))} placeholder={t('admin.offices.calle_123_ciudad')} className="h-8" /></TableCell>
      <TableCell><Input value={draft.contactName} onChange={(e) => setDraft((d) => ({ ...d, contactName: e.target.value }))} placeholder="Contacto" className="h-8" /></TableCell>
      <TableCell><Input value={draft.phone} onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))} placeholder="+54 11 ..." className="h-8" /></TableCell>
    </>
  );

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-6xl">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Building2 className="h-6 w-6 text-brand-green" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Oficinas</h1>
            <p className="text-sm text-muted-foreground">
              {t('admin.offices.direcciones_de_las_oficinas_se_usan')}
            </p>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="direcciones">Direcciones</TabsTrigger>
          {omAccess?.canView && <TabsTrigger value="office-manager">{t('admin.offices.office_manager')}</TabsTrigger>}
          {kitsAccess?.canView && <TabsTrigger value="kits">Kits</TabsTrigger>}
        </TabsList>

        <TabsContent value="direcciones" className="mt-4 space-y-4">
      <div className="flex justify-end">
        <Button size="sm" className="gap-1.5" onClick={() => { setEditingId(null); setAdding(true); setDraft(emptyDraft()); }}>
          <Plus className="h-3.5 w-3.5" /> {t('admin.offices.agregar_oficina')}
        </Button>
      </div>

      <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/20 p-3 flex items-start gap-2 text-xs text-amber-700">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>
          <Trans i18nKey="admin.offices.los_cambios_impactan_en" components={{ b: <strong /> }} />{' '}{t('admin.offices.incluidos_los_pedidos_a_quipteams_la')}
        </span>
      </div>

      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center p-16"><WinclapLoader /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Oficina</TableHead>
                  <TableHead>{t('admin.offices.pais')}</TableHead>
                  <TableHead className="min-w-[280px]">{t('admin.offices.direccion')}</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>{t('admin.offices.telefono')}</TableHead>
                  <TableHead>Origen</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {offices.map((o) => (
                  <TableRow key={o.id}>
                    {editingId === o.id ? (
                      <>
                        {draftCells}
                        <TableCell>
                          <Badge variant="secondary" className="text-[10px]">{o.source === 'quipteams' ? 'Quipteams' : 'Manual'}</Badge>
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button size="icon" variant="ghost" className="h-8 w-8" disabled={!canSubmit || saveOffice.isPending} onClick={() => submit(o)} title="Guardar">
                            <Save className="h-4 w-4 text-emerald-600" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditingId(null)} title="Cancelar">
                            <X className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell className="font-medium">{o.name}</TableCell>
                        <TableCell className="text-muted-foreground">{o.country}</TableCell>
                        <TableCell className="text-sm">{o.address}</TableCell>
                        <TableCell className="text-muted-foreground">{o.contactName || '—'}</TableCell>
                        <TableCell className="text-muted-foreground whitespace-nowrap">
                          {o.phone ? `${o.phoneCountryCode ? o.phoneCountryCode + ' ' : ''}${o.phone}` : '—'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5">
                            <Badge variant="secondary" className="text-[10px]">{o.source === 'quipteams' ? 'Quipteams' : 'Manual'}</Badge>
                            {o.overridden && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-300">editada</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => startEdit(o)} title="Editar">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {o.overridden && (
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground" title={t('admin.offices.volver_al_valor_de_quipteams')}
                              disabled={resetOffice.isPending}
                              onClick={() => resetOffice.mutate({ id: o.id, source: 'quipteams' })}>
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                          {o.source === 'custom' && (
                            <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground hover:text-destructive" title={t('admin.offices.borrar_oficina_manual')}
                              disabled={resetOffice.isPending}
                              onClick={() => resetOffice.mutate({ id: o.id, source: 'custom' })}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}

                {adding && (
                  <TableRow>
                    {draftCells}
                    <TableCell><Badge variant="secondary" className="text-[10px]">Manual</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" className="h-8 w-8" disabled={!canSubmit || saveOffice.isPending} onClick={() => submit()} title="Guardar">
                        <Save className="h-4 w-4 text-emerald-600" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setAdding(false)} title="Cancelar">
                        <X className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )}

                {!offices.length && !adding && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-10">
                      {t('admin.offices.sin_oficinas_si_quipteams_no_responde')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
        </TabsContent>

        {omAccess?.canView && (
          <TabsContent value="office-manager" className="mt-4">
            <OfficeManagerBoard />
          </TabsContent>
        )}

        {kitsAccess?.canView && (
          <TabsContent value="kits" className="mt-4">
            <KitsModule />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
